/**
 * JobsModal — Phrase-style job management modal for a project.
 *
 * PM view:  list all jobs, create new, reassign, set deadline, delete.
 * Linguist view: list own jobs with accept/decline/complete actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Plus, Trash2, Check, ChevronDown, Loader2,
  Calendar, User, FileText, AlertCircle, CheckCircle2,
  Clock, XCircle, PlayCircle, PackageCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dynamic step badge colors (cycles for N steps)
const STEP_COLORS = [
  'bg-blue-50 text-blue-700',
  'bg-orange-50 text-orange-700',
  'bg-purple-50 text-purple-700',
  'bg-green-50 text-green-700',
  'bg-pink-50 text-pink-700',
  'bg-teal-50 text-teal-700',
];
function stepColor(stepNum) { return STEP_COLORS[(stepNum - 1) % STEP_COLORS.length]; }

const STATUS_CONFIG = {
  new:          { label: 'New',          icon: Clock,         cls: 'bg-blue-50 text-blue-700 border-blue-100' },
  accepted:     { label: 'Accepted',     icon: PlayCircle,    cls: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
  in_progress:  { label: 'In Progress',  icon: PlayCircle,    cls: 'bg-amber-50 text-amber-700 border-amber-100' },
  completed:    { label: 'Completed',    icon: CheckCircle2,  cls: 'bg-green-50 text-green-700 border-green-100' },
  delivered:    { label: 'Delivered',    icon: PackageCheck,  cls: 'bg-purple-50 text-purple-700 border-purple-100' },
  declined:     { label: 'Declined',     icon: XCircle,       cls: 'bg-red-50 text-red-700 border-red-100' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
};

const deadlineBadge = (deadline) => {
  if (!deadline) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const due = new Date(deadline);
  const days = Math.round((due - today) / 86400000);
  if (days < 0)  return { text: `${Math.abs(days)}d overdue`, cls: 'text-red-600' };
  if (days <= 2) return { text: `Due in ${days}d`,             cls: 'text-amber-600' };
  return { text: due.toLocaleDateString('en-IN', { month:'short', day:'numeric' }), cls: 'text-gray-500' };
};

export default function JobsModal({ project, user, onClose }) {
  const { isDarkMode } = useTheme();
  const [jobs, setJobs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  const isPM = user?.role === 'owner' || user?.role === 'pm' || project?.email === user?.email;
  const token = localStorage.getItem('token');
  const [stats, setStats] = useState(null);
  // N2: project's configured workflow steps (default to Translation+Review if not set)
  const stepOptions = (project?.workflow_steps || [{ step: 1, name: 'Translation' }, { step: 2, name: 'Review' }]);

  const [form, setForm] = useState({
    file_name: '', source_lang: project?.source_lang || 'English',
    target_lang: project?.target_lang || 'Hindi',
    workflow_step: 1, assigned_to: '', deadline: '',
    word_count: 0, notes: '',
  });

  // Load jobs + team members
  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/jobs/project/${project.project_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/jobs/project/${project.project_id}/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (jobsRes.ok)  setJobs(await jobsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [project.project_id, token]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    if (!isPM) return;
    // Load team members for assign dropdown
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/team`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const members = (data.members || []).filter(m => m.status === 'active');
          setTeamMembers(members);
        }
      } catch (e) { /* non-critical */ }
    })();
  }, [isPM, token]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.file_name.trim()) { toast.error('File name required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, project_id: project.project_id,
          word_count: parseInt(form.word_count) || 0 })
      });
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Create failed'); return; }
      const created = await res.json();
      setJobs(prev => [...prev, created]);
      toast.success('Job created');
      setShowCreate(false);
      setForm(f => ({ ...f, file_name: '', notes: '', word_count: 0, assigned_to: '', deadline: '' }));
    } catch (e) { toast.error('Create failed'); }
    setSubmitting(false);
  };

  const jobAction = async (jobId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/jobs/${jobId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Action failed'); return; }
      const updated = await res.json();
      setJobs(prev => prev.map(j => j.job_id === jobId ? updated : j));
      toast.success(`Job ${action}ed`);
    } catch (e) { toast.error('Action failed'); }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      const res = await fetch(`${API_URL}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Delete failed'); return; }
      setJobs(prev => prev.filter(j => j.job_id !== jobId));
      toast.success('Job deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleReassign = async (jobId, newEmail) => {
    try {
      const res = await fetch(`${API_URL}/api/jobs/${jobId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ assigned_to: newEmail })
      });
      if (!res.ok) { toast.error('Reassign failed'); return; }
      const updated = await res.json();
      setJobs(prev => prev.map(j => j.job_id === jobId ? updated : j));
      toast.success('Reassigned');
    } catch (e) { toast.error('Reassign failed'); }
  };

  const base = isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900';
  const card = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const input = isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';
  const th = isDarkMode ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl ${base}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className="text-lg font-bold">Jobs — {project.name}</h2>
            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {jobs.length} job{jobs.length !== 1 ? 's' : ''} · assign files to translators &amp; reviewers
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isPM && (
              <button
                onClick={() => setShowCreate(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} /> New Job
              </button>
            )}
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Create Job Form */}
        {showCreate && (
          <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-blue-50/50'}`}>
            <h3 className="text-sm font-semibold mb-3">Create New Job</h3>
            <form onSubmit={handleCreate} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>File / Task Name *</label>
                <input className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
                  placeholder="e.g. contract_v2.pdf" value={form.file_name}
                  onChange={e => setForm(f => ({ ...f, file_name: e.target.value }))} />
              </div>
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Workflow Step</label>
                <select className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
                  value={form.workflow_step} onChange={e => setForm(f => ({ ...f, workflow_step: parseInt(e.target.value) }))}>
                  {stepOptions.map(s => <option key={s.step} value={s.step}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Assign To</label>
                <select className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
                  value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                  <option value="">— Unassigned —</option>
                  {teamMembers.map(m => (
                    <option key={m.email} value={m.email}>{m.name || m.email} ({m.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Deadline</label>
                <input type="date" className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
                  value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
              </div>
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Word Count</label>
                <input type="number" min="0" className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
                  placeholder="0" value={form.word_count}
                  onChange={e => setForm(f => ({ ...f, word_count: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Notes</label>
                <input className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
                  placeholder="Optional instructions or context..." value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="col-span-2 flex gap-2 justify-end">
                <button type="button" onClick={() => setShowCreate(false)}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Create Job
                </button>
              </div>
            </form>
          </div>
        )}

        {/* I5 — PM Analytics Stats Bar */}
        {stats && stats.total_jobs > 0 && (
          <div className={`px-6 py-3 border-b grid grid-cols-2 sm:grid-cols-4 gap-3 ${isDarkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50/80'}`}>
            {[
              { label: 'Total Words',   value: stats.total_words.toLocaleString(),            cls: '' },
              { label: 'Words Done',    value: `${stats.completed_words.toLocaleString()} (${stats.progress_pct}%)`, cls: stats.progress_pct === 100 ? 'text-green-600' : '' },
              { label: 'Overdue Jobs',  value: stats.overdue_count,                           cls: stats.overdue_count > 0 ? 'text-red-600' : '' },
              { label: 'Total Jobs',    value: stats.total_jobs,                              cls: '' },
            ].map(({ label, value, cls }) => (
              <div key={label}>
                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
                <p className={`text-base font-bold ${cls || (isDarkMode ? 'text-gray-200' : 'text-gray-800')}`}>{value}</p>
              </div>
            ))}
            {/* Per-assignee mini-bars */}
            {stats.assignees?.filter(a => a.total_jobs > 0).map(a => {
              const pct = a.total_words > 0 ? Math.round(a.completed_words / a.total_words * 100) : 0;
              return (
                <div key={a.email} className="col-span-2 sm:col-span-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{a.name || a.email?.split('@')[0]}</span>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{a.completed_jobs}/{a.total_jobs} jobs · {a.total_words.toLocaleString()} words · {pct}%</span>
                  </div>
                  <div className={`h-1.5 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Jobs Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40 gap-2">
              <Loader2 size={20} className="animate-spin text-blue-500" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading jobs...</span>
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <FileText size={36} className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No jobs yet</p>
              {isPM && <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Click "New Job" to assign work to your team</p>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  {['File / Task','Step','Assigned To','Status','Deadline','Words','Actions'].map(h => (
                    <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider border-b ${th}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-100'}`}>
                {jobs.map(job => {
                  const dl = deadlineBadge(job.deadline);
                  const isMyJob = job.assigned_to === user?.email;
                  return (
                    <tr key={job.job_id} className={`transition-colors ${isDarkMode ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'}`}>
                      {/* File */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-blue-500 flex-shrink-0" />
                          <div>
                            <p className="font-medium leading-tight truncate max-w-[160px]">{job.file_name}</p>
                            {job.notes && <p className={`text-xs truncate max-w-[160px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{job.notes}</p>}
                          </div>
                        </div>
                      </td>
                      {/* Step */}
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${stepColor(job.workflow_step)}`}>{job.step_name}</span>
                      </td>
                      {/* Assigned To */}
                      <td className="px-4 py-3">
                        {isPM ? (
                          <select
                            className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 ${input}`}
                            value={job.assigned_to || ''}
                            onChange={e => handleReassign(job.job_id, e.target.value)}
                          >
                            <option value="">— Unassigned —</option>
                            {teamMembers.map(m => (
                              <option key={m.email} value={m.email}>{m.name || m.email}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {job.assigned_name || job.assigned_to || '—'}
                          </span>
                        )}
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                      {/* Deadline */}
                      <td className="px-4 py-3">
                        {dl ? (
                          <span className={`text-xs font-medium flex items-center gap-1 ${dl.cls}`}>
                            <Calendar size={11} /> {dl.text}
                          </span>
                        ) : <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>}
                      </td>
                      {/* Words */}
                      <td className={`px-4 py-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {job.word_count > 0 ? job.word_count.toLocaleString() : '—'}
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* Linguist actions */}
                          {isMyJob && job.status === 'new' && (
                            <>
                              <button onClick={() => jobAction(job.job_id, 'accept')}
                                className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded font-medium transition-colors">
                                Accept
                              </button>
                              <button onClick={() => jobAction(job.job_id, 'decline')}
                                className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded font-medium transition-colors">
                                Decline
                              </button>
                            </>
                          )}
                          {isMyJob && (job.status === 'accepted' || job.status === 'in_progress') && (
                            <button onClick={() => jobAction(job.job_id, 'complete')}
                              className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded font-medium transition-colors flex items-center gap-1">
                              <Check size={11} /> Complete
                            </button>
                          )}
                          {/* PM actions */}
                          {isPM && job.status === 'completed' && (
                            <button onClick={() => jobAction(job.job_id, 'deliver')}
                              className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded font-medium transition-colors">
                              Deliver
                            </button>
                          )}
                          {isPM && (
                            <button onClick={() => handleDelete(job.job_id)}
                              className={`p-1 rounded transition-colors ${isDarkMode ? 'text-gray-500 hover:text-red-400 hover:bg-gray-700' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                              title="Delete job">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer summary */}
        {jobs.length > 0 && (
          <div className={`px-6 py-3 border-t text-xs flex gap-4 ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
            {Object.entries(
              jobs.reduce((acc, j) => { acc[j.status] = (acc[j.status] || 0) + 1; return acc; }, {})
            ).map(([st, n]) => (
              <span key={st}><span className="font-semibold">{n}</span> {STATUS_CONFIG[st]?.label || st}</span>
            ))}
            <span className="ml-auto">
              {jobs.reduce((s, j) => s + (j.word_count || 0), 0).toLocaleString()} total words
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
